import React, { useState, useEffect, useRef } from 'react';
import TextBox from 'devextreme-react/text-box';
import Button from 'devextreme-react/button';
import { LoadPanel } from 'devextreme-react/load-panel';
import notify from 'devextreme/ui/notify';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { FaEdit, FaStackExchange, FaPrint, FaAmazon, FaArrowRight, FaCopy  } from "react-icons/fa";

const ApplicantSender = () => {
  const [applicant, setApplicantData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applicationNo, setApplicationNo] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const inputRef = useRef(null);
  const [clipboardData, setClipboardData] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (id) {
      fetchApplicantData();
    }
    return () => {
      // Clean up the URL when component unmounts
      if (photoUrl) {
        setPhotoUrl('');
      }
    };
  }, [id]);

  const copyManually = (inputRef) => {
    if (inputRef.current) {
      inputRef.current.select();
      document.execCommand("copy");
    }
  };

  const fetchPhotoFromB2 = async (photoKey) => {
    try {
      if (!photoKey) {
        console.log('No photo key provided');
        return null;
      }

      const response = await api.get(`/applicants/file-url?fileKey=${encodeURIComponent(photoKey)}`);
      
      if (response.data && response.data.url) {
        return response.data.url;
      } else {
        console.error('No URL returned from B2 service');
        return null;
      }
    } catch (error) {
      console.error('Error fetching photo from B2:', error);
      return null;
    }
  };

  const fetchApplicantData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/applicants/${id}`);
  
      const data = response.data;
      setApplicantData(data);
      setApplicationNo(data.ApplicationNo || '');
  
      // Handle photo from Backblaze B2 using PhotoKey
      if (data.photoKey) {
        try {
          const b2PhotoUrl = await fetchPhotoFromB2(data.photoKey);
          if (b2PhotoUrl) {
            setPhotoUrl(b2PhotoUrl);
          } else {
            console.warn('Could not load photo from B2');
            setPhotoUrl(''); // Clear any previous photo
          }
        } catch (error) {
          console.error('Error processing photo from B2:', error);
          setPhotoUrl(''); // Clear any previous photo on error
        }
      } else {
        // No photo key available
        setPhotoUrl('');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Failed to fetch applicant data';
      
      notify({
        message: `Error loading applicant data: ${errorMessage}`,
        type: 'error',
        displayTime: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (showNotification = true) => {
    try {
      if (!id) {
        console.error("Error: No applicant ID provided.");
        return;
      }
  
      const response = await api.get(`/applicants/get_encripted?id=${encodeURIComponent(id)}`, {
        responseType: 'text' // Important for non-JSON responses
      });
  
      const data = response.data;
      setClipboardData(data);
  
      try {
        await navigator.clipboard.writeText(data);
        if (showNotification) {
          notify({
            message: 'Applicant data is ready to be imported',
            type: 'success',
            displayTime: 3000,
            width: "100px",
            position: {
              my: 'top center',
              at: 'top center',
              of: '#page-notification-container'
            }
          });
        }
      } catch (clipboardError) {
        console.warn('Clipboard API failed, falling back to execCommand');
        const textArea = document.createElement('textarea');
        textArea.value = data;
        textArea.style.position = 'fixed';
        document.body.appendChild(textArea);
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          if (!successful) {
            throw new Error('execCommand copy failed');
          }
          if (showNotification) {
            notify({
              message: 'The Applicant is Sent Successfully!',
              type: 'success',
              displayTime: 3000
            });
          }
        } catch (execError) {
          console.error('Copy failed:', execError);
          notify({
            message: 'Failed to copy data to clipboard',
            type: 'error',
            displayTime: 5000
          });
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'An error occurred. Please try again.';
      
      notify({
        message: errorMessage,
        type: 'error',
        displayTime: 5000
      });
    }
  };

  const handleEdit = async () => {
    try {
      setLoading(true);
      
      if (!id || !applicationNo) {
        notify({
          message: 'Application Number is required',
          type: 'error',
          displayTime: 3000,
          width: "100px",
          position: {
            my: 'top center',
            at: 'top center',
            of: '#page-notification-container'
          }
        });
        return;
      }
  
      await api.post(`/Applicants/editEnjazNo/${id},${applicationNo}`);
  
      if (applicant && applicant.visaNo !== "" && applicant.sponsorID !== "") {
        navigate(`/reports/applicantreport/${id}`);
      } else {
        navigate("/applicants/applicantlist");
      }
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to update application number';
      
      notify({
        message: errorMessage,
        type: 'error',
        displayTime: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendThenEdit = async () => {
    await handleSend(false);
    navigate(`/applicants/edit/${id}`);
  };

  return (
    <div className="container mt-4">
      <div 
              style={{ 
                position: 'sticky',
                top: 77,
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: "40px",
                background: 'white',
                zIndex: 1000,
                borderBottom: '1px solid #eee',
                gap: '40px',
              }}
            >
              <h5 
                className="es-label-title" 
                style={{ paddingTop: '15px' }}
              >
                Applicant Information
              </h5>
              
              <div style={{ display: 'flex', gap: '8px' }}>
              <button
                  className='es-toolbar-button'
                  type="default"
                  stylingMode="contained"
                  onClick={() => handleSend(true)}
                  disabled={loading}
                >
                  <FaCopy size={20} /> Send
                  </button>

                <button
                  className='es-toolbar-button'
                  type="success"
                  stylingMode="contained"
                  onClick={handleEdit}
                  disabled={loading}
                >
                  <FaPrint size={20} /> Edit & Print
                  </button>

                <button
                  className='es-toolbar-button'
                  type="normal"
                  stylingMode="contained"
                  onClick={handleSendThenEdit}
                  disabled={loading}
                >
                  <FaEdit size={20} /> Send & Edit
                  </button>
              <a
                  href="https://tawtheeq.musaned.com.sa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="es-toolbar-button"
                  style={{
                    width: '35px',
                    height: '35px',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                  title="Musaned" 
                >
                  <img src="/musaned24.png" alt="Musaned" style={{ width: '20px', height: '20px' }} />
                </a>

                <a
                  href="https://visa.mofa.gov.sa/Account/Login/enjazcompany"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="es-toolbar-button"
                  style={{
                    width: '35px',
                    height: '35px',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                  title="Login to visa.mofa"
                >
                  <img src="/enjaz24.png" alt="Musaned" style={{ width: '20px', height: '20px' }} />
                </a> 
                
                <a
                  href="https://visa.mofa.gov.sa/SmartForm/TraditionalApp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="es-toolbar-button"
                  style={{
                    width: '35px',
                    height: '35px',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                  title="Create new visa"
                >
                  <img src="/enjaz32.png" alt="Musaned" style={{ width: '20px', height: '20px' }} />
                </a>                
              </div>
            </div>


        {loading && !applicant ? (
            <div className="loading-container">Loading applicant data...</div>
        ) : (
        <div className="card p-3 es-field-label-black">
          <div className="row mb-0">
            <div className="col-md-2">
              <div className="row mb-0">
                {photoUrl ? (
                  <div className="photo-container">
                    <img 
                      src={photoUrl} 
                      alt="Applicant" 
                      className="applicant-photo"
                      onError={(e) => {
                        console.error('Error loading photo from B2');
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="photo-placeholder" style={{display: 'none'}}>
                      Photo Load Failed
                    </div>
                  </div>
                ) : (
                  <div className="photo-placeholder">
                    No Photo Available
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-5">
                <div className="row mb-0">
                    <p style={{ marginBottom: "5px" }}>Full Name : <strong style={{ paddingLeft: "33px" }}>{applicant ? applicant.fullName : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Passport No.: <strong style={{ paddingLeft: "19px" }}>{applicant ? applicant.passportNo : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Place of Issue: <strong style={{ paddingLeft: "13px" }}>{applicant ? applicant.placeOfBirth : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Date of Expiry: <strong style={{ paddingLeft: "15px" }}>{applicant ? applicant.pasportExpiryDate : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Place of Birth: <strong style={{ paddingLeft: "15px" }}>{applicant ? applicant.placeOfBirth : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Date of Birth: <strong style={{ paddingLeft: "20px" }}>{applicant ? applicant.dateOfBirth : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Gender: <strong style={{ paddingLeft: "53px" }}>{applicant ? applicant.gender : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Region: <strong style={{ paddingLeft: "54px" }}>{applicant ? applicant.region_ : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Woreda: <strong style={{ paddingLeft: "50px" }}>{applicant ? applicant.kebele : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Marital Status:	 <strong style={{ paddingLeft: "13px" }}>{applicant ? applicant.maritalStatus : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Religion: <strong style={{ paddingLeft: "47px" }}>{applicant ? applicant.religion : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Phone No.: <strong style={{ paddingLeft: "33px" }}>{applicant ? applicant.phoneNo : "Loading..."}</strong></p>
                    <p style={{ marginBottom: "5px" }}>Labor ID: <strong style={{ paddingLeft: "45px" }}>{applicant ? applicant.laborID : "Loading..."}</strong></p>
                </div>
            </div>

            <div className="col-md-5">
              <div className="row mb-0">
                <p style={{ marginBottom: "5px" }}>Relative Name : <strong style={{ paddingLeft: "15px" }}>{applicant ? applicant.contactPerson : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>R-Birth Date: <strong style={{ paddingLeft: "34px" }}>{applicant ? applicant.contactBirthDate : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>R.Region: <strong style={{ paddingLeft: "51px" }}>{applicant ? applicant.contactAddress : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>R.Zone: <strong style={{ paddingLeft: "62px" }}>{applicant ? applicant.contactSubCity : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>R.Woreda: <strong style={{ paddingLeft: "46px" }}>{applicant ? applicant.contactWereda : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>R.Gender: <strong style={{ paddingLeft: "49px" }}>{applicant ? applicant.contactGender : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>R.Phone: <strong style={{ paddingLeft: "54px" }}>{applicant ? applicant.contactPhone : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>Occupation: <strong style={{ paddingLeft: "37px" }}>{applicant ? applicant.occupation : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>Sponsor Name: <strong style={{ paddingLeft: "17px" }}>{applicant ? applicant.sponsorName : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>Sponsor ID: <strong style={{ paddingLeft: "39px" }}>{applicant ? applicant.sponsorID : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>Sponsor Address: <strong style={{ paddingLeft: "5px" }}>{applicant ? applicant.sponsorAddress : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>Sponsor Phone: <strong style={{ paddingLeft: "15px" }}>{applicant ? applicant.sponsorPhone : "Loading..."}</strong></p>
                <p style={{ marginBottom: "5px" }}>Agent: <strong style={{ paddingLeft: "33px" }}>{applicant ? applicant.officeAbroad : "Loading..."}</strong></p>
              </div>
            </div>
          </div>
          
          <hr />
          
          <div className="row mb-0">
            <div className="col-md-2">
              <div className="row mb-0">
                <input ref={inputRef} value={clipboardData} readOnly className="hidden-input" />     
              </div>
            </div>
            <div className="col-md-1">
              <div className="row mb-2" style={{paddingTop:"5px"}}>
                  <label>Application#:</label>                
              </div>
            </div>
            <div className="col-md-3">
              <div className="row mb-0">
                <TextBox
                  value={applicationNo}
                  onValueChanged={(e) => setApplicationNo(e.value)}
                />
              </div>
            </div>

            <div className="col-md-4">
            <div id="page-notification-container"></div>
            </div>
          </div>
        </div>
      )}
      
      
      <LoadPanel
        visible={loading && applicant}
        showIndicator={true}
        showPane={true}
        shading={true}
        closeOnOutsideClick={false}
      />
      
      <style jsx>{`
        .loading-container {
          padding: 20px;
          text-align: center;
        }
        
        .photo-container {
          margin-bottom: 20px;
          text-align: center;
        }
        
        .applicant-photo {
          max-width: 150px;
          max-height: 150px;
          object-fit: cover;
          border-radius: 4px;
        }
        
        .photo-placeholder {
          width: 150px;
          height: 150px;
          background-color: #f5f5f5;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          border-radius: 4px;
        }
        
        .info-row {
          margin-bottom: 5px;
        }
        
        .action-buttons {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 20px;
        }
        
        .hidden-input {
          opacity: 0;
          height: 0;
          width: 0;
          border: none;
          padding: 0;
        }
      `}</style>
    </div>
  );
};

export default ApplicantSender;